import { Box, Stack, useTheme } from "@mui/material";
import { FormBox, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { DisplayUsers } from "./DisplayUsers";
import { SearchUserBar } from "./SearchUserBar";
import { Company } from "src/api/client-api/company/useGetOneOrganization.ts";
import { SupplierFormAppearance, SupplierFormType } from "../../SupplierForm.types";
import { SectionCard, SectionContent, SectionHeader } from "src/components/EntityForm";

type Page2Props = {
    appearance?: SupplierFormAppearance;
    supplier: SupplierFormType;
    setSupplier: (newSupplier: SupplierFormType) => void;
    existingSupplier?: Company;
    progress?: { current: number; total: number };
};
const Page2 = (props: Page2Props) => {
    if (props.appearance !== "product-v2") {
        return <LegacyPage2 {...props} />;
    }

    return <ProductV2Page2 {...props} />;
};

const ProductV2Page2 = (props: Page2Props) => {
    const { supplier, setSupplier } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <SectionCard data-testid="supplier-contributors-section">
            <SectionHeader
                progress={props.progress}
                title={t("utilisateurs", "Contributors")}
                subtitle={t(
                    "supplier.form.contributors.subtitle",
                    "Select the users who can contribute to this supplier.",
                )}
            />
            <SectionContent
                data-testid="supplier-contributors-content"
                gap={theme.spacing(2)}
                sx={{ paddingBottom: theme.spacing(2) }}
            >
                <Box
                    data-testid="supplier-contributor-search-grid"
                    sx={{
                        display: "grid",
                        gap: theme.spacing(3),
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        [theme.breakpoints.down("md")]: {
                            gridTemplateColumns: "minmax(0, 1fr)",
                        },
                    }}
                >
                    <Stack gap={theme.spacing(1)} minWidth={0} width="100%">
                        <Typography variant="body-semibold">{t("recherchez_parmi_les_utilisateurs")}</Typography>
                        <SearchUserBar
                            appearance="product-v2"
                            selectedUsers={supplier.users}
                            addUser={(newUser) => {
                                const newUsers = [...supplier.users, newUser];
                                setSupplier({
                                    ...supplier,
                                    users: newUsers,
                                });
                            }}
                        />
                    </Stack>
                </Box>
                <DisplayUsers
                    appearance="product-v2"
                    users={supplier.users}
                    onDelete={(userId) =>
                        setSupplier({
                            ...supplier,
                            users: supplier.users.filter((user) => user["@id"] !== userId),
                        })
                    }
                />
            </SectionContent>
        </SectionCard>
    );
};

const LegacyPage2 = (props: Page2Props) => {
    const { supplier, setSupplier } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Box sx={{ display: "flex", gap: theme.spacing(2) }}>
            <FormBox sx={{ display: "flex", flex: 3, flexDirection: "column", gap: theme.spacing(2) }}>
                <Typography variant="big-medium">{t("recherchez_parmi_les_utilisateurs")}</Typography>
                <SearchUserBar
                    appearance="legacy"
                    selectedUsers={supplier.users}
                    addUser={(newUser) => {
                        setSupplier({ ...supplier, users: [...supplier.users, newUser] });
                    }}
                />
                <Box>
                    <DisplayUsers
                        appearance="legacy"
                        users={supplier.users}
                        onDelete={(userId) =>
                            setSupplier({
                                ...supplier,
                                users: supplier.users.filter((user) => user["@id"] !== userId),
                            })
                        }
                    />
                </Box>
            </FormBox>
        </Box>
    );
};
export default Page2;
